import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ExternalLink, RefreshCw, Link2, Unlink, Upload, ChevronRight,
  CheckCircle2, AlertTriangle, Package, History,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Channel Listings page. Lives under /integrations/channel-listings.
 * Three tabs:
 *   • Mapped    — push, toggle sync, view, unmap
 *   • Unmapped  — manual SKU mapping
 *   • Failed    — retry failed pushes (single or bulk)
 */
const ChannelListingsPage = () => {
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [activeTab, setActiveTab] = useState('mapped');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ mapped: 0, unmapped: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Manual mapping state
  const [mapDialog, setMapDialog] = useState(null);        // the listing being mapped
  const [mapTargetSku, setMapTargetSku] = useState('');

  // Bulk retry selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Sync history slide-over — stores the listing being viewed +
  // its loaded entries. Populated by `openHistory(listing)`.
  const [historyFor, setHistoryFor] = useState(null); // { listing, entries, loading }

  const token = () => localStorage.getItem('token');

  const loadChannels = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/sales-channels`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const ch = r.data.channels || [];
      setChannels(ch);
      if (ch.length > 0 && !selectedChannelId) setSelectedChannelId(ch[0].id);
    } catch (e) {
      toast.error('Failed to load channels');
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const [lr, sr] = await Promise.all([
        axios.get(`${API_BASE_URL}/channel-listings`, {
          headers: { Authorization: `Bearer ${token()}` },
          params: {
            channel_id: selectedChannelId || undefined,
            status: activeTab,
            search: search || undefined,
            limit: 200,
          },
        }),
        axios.get(`${API_BASE_URL}/channel-listings/stats`, {
          headers: { Authorization: `Bearer ${token()}` },
          params: { channel_id: selectedChannelId || undefined },
        }),
      ]);
      setItems(lr.data.items || []);
      setStats(sr.data || { mapped: 0, unmapped: 0, failed: 0 });
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChannels(); }, []);
  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [selectedChannelId, activeTab]);

  const doUnmap = async (listing) => {
    try {
      await axios.post(`${API_BASE_URL}/channel-listings/${listing.id}/unmap`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success('Listing unmapped');
      loadItems();
    } catch (e) { toast.error('Unmap failed'); }
  };

  const doToggleSync = async (listing, v) => {
    try {
      await axios.post(`${API_BASE_URL}/channel-listings/${listing.id}/toggle-sync`,
        { sync_enabled: v },
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success(v ? 'Sync enabled' : 'Sync disabled');
      loadItems();
    } catch (e) { toast.error('Toggle failed'); }
  };

  const doPushNow = async (listing) => {
    try {
      const r = await axios.post(`${API_BASE_URL}/channel-listings/${listing.id}/push-inventory-now`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (r.data.ok) {
        toast.success(`Pushed ${r.data.pushed_quantity} to ${listing.channel_type}`);
      } else {
        toast.error(`Push failed: ${r.data.error || 'unknown error'}`);
      }
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Push failed');
    }
  };

  const submitMapping = async () => {
    if (!mapDialog || !mapTargetSku.trim()) return;
    try {
      await axios.post(`${API_BASE_URL}/channel-listings/${mapDialog.id}/map`,
        { mapped_sku: mapTargetSku.trim() },
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Listing mapped');
      setMapDialog(null);
      setMapTargetSku('');
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mapping failed');
    }
  };

  const doBulkRetry = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('Select at least one row'); return; }
    try {
      const r = await axios.post(`${API_BASE_URL}/channel-listings/bulk-retry-failed`,
        { listing_ids: ids },
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success(`Retried ${r.data.retried} — ${r.data.succeeded} succeeded, ${r.data.failed} failed`);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
    }
  };

  const viewInInventory = (sku) => {
    if (!sku) return;
    window.open(`/inventory?search=${encodeURIComponent(sku)}`, '_blank');
  };

  const openHistory = async (listing) => {
    // Opens the History slide-over and loads recent push-log entries
    // for this listing (both manual pushes AND automatic fan-outs).
    setHistoryFor({ listing, entries: [], loading: true });
    try {
      const r = await axios.get(
        `${API_BASE_URL}/channel-listings/${listing.id}/push-history?limit=100`,
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      setHistoryFor({ listing, entries: r.data?.entries || [], loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
      setHistoryFor({ listing, entries: [], loading: false, error: true });
    }
  };

  const toggleSelected = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  return (
    <div className="p-8 space-y-6" data-testid="channel-listings-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary flex items-center gap-2">
            <Package className="h-6 w-6" /> Channel Listings
          </h1>
          <p className="text-sm text-wt-text-muted mt-1">
            Products synced from each sales channel. Map them to your Opswarex SKUs so inventory
            sync and order routing work correctly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedChannelId || 'all'} onValueChange={(v) => setSelectedChannelId(v === 'all' ? '' : v)}>
            <SelectTrigger
              data-testid="cl-channel-filter"
              className="w-56 bg-wt-bg border-wt-border text-wt-text-primary"
            >
              <SelectValue placeholder="All channels" />
            </SelectTrigger>
            <SelectContent className="bg-wt-paper border-wt-border text-wt-text-primary">
              <SelectItem value="all">All channels</SelectItem>
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} ({c.channel_type})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            data-testid="cl-search"
            placeholder="Search SKU / title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadItems()}
            className="w-64"
          />
          <Button variant="outline" onClick={loadItems} data-testid="cl-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="mapped" data-testid="cl-tab-mapped">
            Mapped <Badge variant="outline" className="ml-2">{stats.mapped}</Badge>
          </TabsTrigger>
          <TabsTrigger value="unmapped" data-testid="cl-tab-unmapped">
            Unmapped <Badge variant="outline" className="ml-2 text-amber-400">{stats.unmapped}</Badge>
          </TabsTrigger>
          <TabsTrigger value="failed" data-testid="cl-tab-failed">
            Failed <Badge variant="outline" className="ml-2 text-red-400">{stats.failed}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* -------- Mapped -------- */}
        <TabsContent value="mapped" className="mt-4">
          <div className="border border-wt-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-wt-bg text-left text-wt-text-muted">
                <tr>
                  <th className="p-3">Channel Product</th>
                  <th className="p-3">Channel SKU</th>
                  <th className="p-3">Mapped SKU</th>
                  <th className="p-3 text-center">Sync</th>
                  <th className="p-3">Last push</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="p-8 text-center text-wt-text-muted">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-wt-text-muted">No mapped listings yet.</td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} className="border-t border-wt-border" data-testid={`cl-row-${it.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {it.channel_image_url && (
                          <img src={it.channel_image_url} alt="" className="h-8 w-8 rounded object-cover border border-wt-border" />
                        )}
                        <div>
                          <div className="text-wt-text-primary">{it.channel_product_title || '-'}</div>
                          <div className="text-xs text-wt-text-muted capitalize">{it.channel_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{it.channel_sku}</td>
                    <td className="p-3 font-mono text-xs text-green-400">{it.mapped_sku}</td>
                    <td className="p-3 text-center">
                      <Switch
                        checked={it.sync_enabled}
                        onCheckedChange={(v) => doToggleSync(it, v)}
                        data-testid={`cl-sync-${it.id}`}
                      />
                    </td>
                    <td className="p-3 text-xs text-wt-text-muted">
                      {it.last_sync_at ? new Date(it.last_sync_at).toLocaleString() : '—'}
                      {it.last_sync_status === 'succeeded' && (
                        <CheckCircle2 className="inline h-3 w-3 ml-1 text-green-400" />
                      )}
                      {it.last_sync_status === 'failed' && (
                        <AlertTriangle className="inline h-3 w-3 ml-1 text-red-400" />
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => doPushNow(it)} data-testid={`cl-push-${it.id}`}>
                          <Upload className="h-3 w-3 mr-1" /> Push
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => openHistory(it)}
                          title="Sync history (manual + automatic)"
                          data-testid={`cl-history-${it.id}`}
                        >
                          <History className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => viewInInventory(it.mapped_sku)} data-testid={`cl-view-${it.id}`}>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => doUnmap(it)} data-testid={`cl-unmap-${it.id}`}>
                          <Unlink className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* -------- Unmapped -------- */}
        <TabsContent value="unmapped" className="mt-4">
          <div className="border border-wt-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-wt-bg text-left text-wt-text-muted">
                <tr>
                  <th className="p-3">Channel Product</th>
                  <th className="p-3">Channel SKU</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="p-8 text-center text-wt-text-muted">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={3} className="p-8 text-center text-wt-text-muted">Everything is mapped 🎉</td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} className="border-t border-wt-border" data-testid={`cl-unmapped-row-${it.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {it.channel_image_url && (
                          <img src={it.channel_image_url} alt="" className="h-8 w-8 rounded object-cover border border-wt-border" />
                        )}
                        <div>
                          <div className="text-wt-text-primary">{it.channel_product_title || '-'}</div>
                          <div className="text-xs text-wt-text-muted capitalize">{it.channel_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{it.channel_sku}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" onClick={() => { setMapDialog(it); setMapTargetSku(it.channel_sku || ''); }} data-testid={`cl-map-${it.id}`}>
                        <Link2 className="h-3 w-3 mr-1" /> Map to SKU
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* -------- Failed -------- */}
        <TabsContent value="failed" className="mt-4">
          <div className="flex justify-end mb-2">
            <Button onClick={doBulkRetry} disabled={selectedIds.size === 0} data-testid="cl-bulk-retry">
              <RefreshCw className="h-4 w-4 mr-2" /> Retry selected ({selectedIds.size})
            </Button>
          </div>
          <div className="border border-wt-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-wt-bg text-left text-wt-text-muted">
                <tr>
                  <th className="p-3 w-8"></th>
                  <th className="p-3">Channel Product</th>
                  <th className="p-3">SKU</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Attempts</th>
                  <th className="p-3">Error</th>
                  <th className="p-3">Last attempt</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-wt-text-muted">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-wt-text-muted">No failed pushes in the last 24h 🎉</td></tr>
                ) : items.map((it) => {
                  const listingId = it.listing?.id;
                  return (
                    <tr key={it.push_log_id || `${it.channel_id}-${it.sku}`}
                        className="border-t border-wt-border"
                        data-testid={`cl-failed-row-${it.push_log_id}`}>
                      <td className="p-3">
                        {listingId && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(listingId)}
                            onChange={() => toggleSelected(listingId)}
                            data-testid={`cl-failed-select-${listingId}`}
                          />
                        )}
                      </td>
                      <td className="p-3 text-wt-text-primary">
                        {it.listing?.channel_product_title || '-'}
                        <span className="ml-2 text-xs text-wt-text-muted capitalize">{it.channel_type}</span>
                      </td>
                      <td className="p-3 font-mono text-xs">{it.sku}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={it.status === 'permanent_failure' ? 'text-red-500' : 'text-amber-400'}>
                          {it.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">{it.attempts}</td>
                      <td className="p-3 text-xs text-red-400 max-w-[320px] truncate" title={it.error || ''}>
                        {it.error || '—'}
                      </td>
                      <td className="p-3 text-xs text-wt-text-muted">
                        {it.last_attempt_at ? new Date(it.last_attempt_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Manual Map Dialog */}
      <Dialog open={!!mapDialog} onOpenChange={(v) => !v && setMapDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Map channel SKU to an Opswarex SKU</DialogTitle>
          </DialogHeader>
          {mapDialog && (
            <div className="space-y-3">
              <div className="text-sm text-wt-text-muted">
                Channel: <span className="text-wt-text-primary capitalize">{mapDialog.channel_type}</span>
              </div>
              <div className="text-sm text-wt-text-muted">
                Channel SKU: <span className="font-mono text-wt-text-primary">{mapDialog.channel_sku}</span>
              </div>
              <div>
                <label className="text-sm text-wt-text-muted block mb-1">Map to Opswarex SKU</label>
                <Input
                  data-testid="cl-map-input"
                  value={mapTargetSku}
                  onChange={(e) => setMapTargetSku(e.target.value)}
                  placeholder="e.g. WT-SOF-001"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMapDialog(null)} data-testid="cl-map-cancel">Cancel</Button>
            <Button onClick={submitMapping} data-testid="cl-map-submit">
              <ChevronRight className="h-4 w-4 mr-1" /> Map
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync History Dialog — shows the last 100 push-log entries for
          this listing. Covers both manual pushes (trigger_reason =
          manual_listing_push, manual_bulk_retry) and automatic
          fan-outs (virtual_stock_changed, reservation_created,
          scheduled_inventory_push, …). */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Sync History
              {historyFor?.listing?.channel_product_title && (
                <span className="block text-xs text-wt-text-muted font-normal mt-0.5">
                  {historyFor.listing.channel_product_title} ·{' '}
                  <span className="font-mono">{historyFor.listing.mapped_sku || historyFor.listing.channel_sku}</span>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto" data-testid="cl-history-list">
            {historyFor?.loading ? (
              <div className="p-8 text-center text-wt-text-muted text-sm">Loading…</div>
            ) : !historyFor?.entries || historyFor.entries.length === 0 ? (
              <div className="p-8 text-center text-wt-text-muted text-sm">
                No push attempts recorded for this listing yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-wt-bg text-left text-wt-text-muted sticky top-0">
                  <tr>
                    <th className="p-2">When</th>
                    <th className="p-2">Trigger</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {historyFor.entries.map((e) => {
                    // Map internal trigger codes → human-readable
                    // labels so the history reads like an audit log.
                    const TRIGGER_LABEL = {
                      manual_listing_push: 'Manual push',
                      manual_bulk_retry: 'Manual retry',
                      manual_test_push: 'Manual test push',
                      virtual_stock_changed: 'Auto · virtual stock change',
                      listing_mapped: 'Auto · listing mapped',
                      reservation_created: 'Auto · order placed',
                      reservation_released: 'Auto · order cancelled',
                      inventory_adjustment: 'Auto · stock adjustment',
                      scheduled_inventory_push: 'Auto · scheduled',
                      retry: 'Scheduled retry',
                    };
                    const label = TRIGGER_LABEL[e.trigger_reason] || (e.trigger_reason || '—');
                    const isOk = e.status === 'succeeded';
                    return (
                      <tr key={e.id} className="border-t border-wt-border align-top">
                        <td className="p-2 text-xs text-wt-text-muted whitespace-nowrap">
                          {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
                        </td>
                        <td className="p-2 text-wt-text-primary">{label}</td>
                        <td className="p-2 text-right font-mono">{e.pushed_quantity ?? '—'}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={isOk ? 'text-emerald-400' : (e.status === 'permanent_failure' ? 'text-red-500' : 'text-amber-400')}>
                            {isOk ? 'Success' : e.status}
                          </Badge>
                        </td>
                        <td className="p-2 text-xs max-w-[320px]">
                          {e.error ? (
                            <span className="text-red-400" title={e.error}>{e.error.length > 120 ? e.error.slice(0, 120) + '…' : e.error}</span>
                          ) : (
                            <span className="text-wt-text-muted">
                              {e.attempts > 1 ? `attempt ${e.attempts}` : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHistoryFor(null)} data-testid="cl-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelListingsPage;
